// Pass to another AE (spec C6; the Renewals and Record boards' Pass dialog, Dialog, Dropdown and
// ConfirmPopup cards): the AE picks another account executive and writes a required note, then
// confirms. One or several (the bulk bar), from Needs your answer or With me. Handled by and the
// status stay; the renewal shows Awaiting AE until the new AE accepts. Opened by use-handoff-answers.
import { useEffect, useState } from 'react';
import type { Api, Assignee } from '../auth/api';
import { ConfirmPopup } from '../shell/ConfirmPopup';
import { Dialog } from '../shell/Dialog';
import { CheckIcon, ChevronDownIcon } from '../shell/icons';
import { passConfirm, type AnswerRow } from './ae-handoff-lines';
import { noteMax } from './notes-lines';
import { plural } from './reassign-lines';

export function PassDialog({
  api,
  rows,
  meId,
  busy,
  onClose,
  onPass,
  aes: given,
}: {
  api: Api;
  rows: AnswerRow[];
  meId: string;
  busy: boolean;
  onClose: () => void;
  /** True once passed; the reason it was not, otherwise. */
  onPass: (ae: { id: string; name: string }, note: string) => Promise<string | true>;
  /** The account executives to pick from, when the caller has them (the layout checks). */
  aes?: Assignee[];
}) {
  const [people, setPeople] = useState<Assignee[] | null>(given ?? null);
  const [failed, setFailed] = useState(false);
  const [pick, setPick] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (given) return;
    let live = true;
    api.assignees().then(
      (p) => live && setPeople(p.people),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [api, given]);

  // Every other active AE (the handoff's own AE never; the AE it came from may take it back).
  const aes = (people ?? []).filter((p) => p.role === 'account_executive' && p.id !== meId);
  const ae = aes.find((p) => p.id === pick) ?? null;
  const text = note.trim();
  const one = rows.length === 1 ? rows[0] : null;
  const subline = one
    ? [one.insured, one.carrier ?? 'Carrier not in AMS360', one.line, one.policyNumber].join(' | ')
    : `${plural(rows.length)} selected`;

  const send = async () => {
    if (!ae) return;
    const result = await onPass(ae, text);
    if (result !== true) {
      setConfirming(false);
      setError(result);
    }
  };

  return (
    <>
      <Dialog
        id="pa-t"
        title="Pass to another AE"
        subline={subline}
        onClose={onClose}
        footer={
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className={ae && text ? 'btn btn-primary ml-auto' : 'btn ml-auto'}
              disabled={!ae || !text || busy}
              onClick={() => setConfirming(true)}
            >
              {ae ? `Pass to ${ae.name}` : 'Pass'}
            </button>
          </>
        }
      >
        <div>
          <span className="flab" id="pa-to-l">
            Account executive <span className="req">*</span>
          </span>
          <div className="sel">
            <button
              type="button"
              className="sel-b"
              id="pa-to"
              aria-haspopup="listbox"
              aria-expanded={listOpen}
              aria-labelledby="pa-to-l pa-to"
              disabled={!people}
              onClick={() => setListOpen(!listOpen)}
            >
              <span className="sel-v">
                {ae?.name ?? (failed ? 'Could not load the list' : people ? 'Choose' : 'Loading')}
              </span>
              <ChevronDownIcon className="ico s12" />
            </button>
            {listOpen && (
              <>
                <button
                  type="button"
                  className="pop-scrim"
                  aria-label="Close list"
                  onClick={() => setListOpen(false)}
                />
                <div className="menu sel-m" role="listbox" aria-label="Account executive">
                  {aes.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="mi"
                      role="option"
                      aria-selected={p.id === pick}
                      onClick={() => {
                        setPick(p.id);
                        setListOpen(false);
                      }}
                    >
                      <span className="dd-ck">
                        {p.id === pick && <CheckIcon className="ico s12" />}
                      </span>
                      <span className="col g2" style={{ minWidth: 0 }}>
                        <span>{p.name}</span>
                        {p.outToday && <span className="sel-h">Out today</span>}
                      </span>
                    </button>
                  ))}
                  {aes.length === 0 && (
                    <span className="mi" aria-disabled="true">
                      No other account executive
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          <p className="fnote">They accept first. Until then it shows as Awaiting AE.</p>
        </div>
        <div>
          <label className="flab" htmlFor="pa-note">
            Note <span className="req">*</span>
          </label>
          <textarea
            id="pa-note"
            className="input"
            placeholder="For example: insured wants to bundle with a commercial policy"
            maxLength={noteMax}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        {error && (
          <p className="ferr" role="alert">
            {error}
          </p>
        )}
      </Dialog>
      {confirming && ae && (
        <ConfirmPopup
          {...passConfirm(rows, ae)}
          action={busy ? 'Passing' : 'Pass'}
          busy={busy}
          onConfirm={() => void send()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}
